import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const DeviceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, post, put, loading } = useApi();
  
  const [formData, setFormData] = useState({
    deviceTypeId: '',
    orientationTypeId: '',
    brand: '',
    devType: '',
    other: '',
    startedAt: '',
    endedAt: '',
    isActive: true
  });

  const [deviceTypes, setDeviceTypes] = useState([]);
  const [orientationTypes, setOrientationTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAllData();
  }, [id]);

  const fetchAllData = async () => {
    setLoadingTypes(true);
    
    // Fetch device types
    const deviceTypesRes = await get('/api/device-types');
    if (deviceTypesRes.data) {
      setDeviceTypes(deviceTypesRes.data);
      console.log('Device Types loaded:', deviceTypesRes.data);
    }

    // Fetch orientation types
    const orientationTypesRes = await get('/api/orientation-types');
    if (orientationTypesRes.data) {
      setOrientationTypes(orientationTypesRes.data);
      console.log('Orientation Types loaded:', orientationTypesRes.data);
    }

    // Fetch device data if editing
    if (id) {
      const deviceRes = await get(`/api/devices/${id}`);
      if (deviceRes.data) {
        setFormData({
          deviceTypeId: deviceRes.data.deviceType?.id || '',
          orientationTypeId: deviceRes.data.orientationType?.id || '',
          brand: deviceRes.data.brand || '',
          devType: deviceRes.data.devType || '',
          other: deviceRes.data.other || '',
          startedAt: deviceRes.data.startedAt ? deviceRes.data.startedAt.slice(0, 16) : '',
          endedAt: deviceRes.data.endedAt ? deviceRes.data.endedAt.slice(0, 16) : '',
          isActive: deviceRes.data.isActive !== undefined ? deviceRes.data.isActive : true
        });
      }
    }

    setLoadingTypes(false);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.deviceTypeId) {
      newErrors.deviceTypeId = 'Device type is required';
    }
    if (!formData.orientationTypeId) {
      newErrors.orientationTypeId = 'Orientation type is required';
    }
    if (!formData.brand.trim()) {
      newErrors.brand = 'Brand is required';
    }
    if (!formData.devType.trim()) {
      newErrors.devType = 'Device type is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData = {
      deviceTypeId: parseInt(formData.deviceTypeId),
      orientationTypeId: parseInt(formData.orientationTypeId),
      brand: formData.brand,
      devType: formData.devType,
      other: formData.other,
      startedAt: formData.startedAt || null,
      endedAt: formData.endedAt || null,
      isActive: formData.isActive
    };

    let response;
    if (id) {
      response = await put(`/api/devices/${id}`, submitData);
    } else {
      response = await post('/api/devices', submitData);
    }

    if (!response.error) {
      navigate('/devices/filter');
    }
  };

  if (loadingTypes || (loading && id)) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/devices/filter')}
          className="mr-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {id ? 'Edit Device' : 'Add New Device'}
        </h1>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Device Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Device Type <span className="text-red-500">*</span>
              </label>
              <select
                name="deviceTypeId"
                value={formData.deviceTypeId}
                onChange={handleChange}
                className={`input-field ${errors.deviceTypeId ? 'border-red-500' : ''}`}
              >
                <option value="">Select device type</option>
                {deviceTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.description} - {type.information}
                  </option>
                ))}
              </select>
              {errors.deviceTypeId && (
                <p className="mt-1 text-sm text-red-600">{errors.deviceTypeId}</p>
              )}
              {deviceTypes.length === 0 && (
                <p className="mt-1 text-sm text-yellow-600">No device types found. Please create device types first.</p>
              )}
            </div>

            {/* Orientation Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Orientation Type <span className="text-red-500">*</span>
              </label>
              <select
                name="orientationTypeId"
                value={formData.orientationTypeId}
                onChange={handleChange}
                className={`input-field ${errors.orientationTypeId ? 'border-red-500' : ''}`}
              >
                <option value="">Select orientation type</option>
                {orientationTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.description}
                  </option>
                ))}
              </select>
              {errors.orientationTypeId && (
                <p className="mt-1 text-sm text-red-600">{errors.orientationTypeId}</p>
              )}
              {orientationTypes.length === 0 && (
                <p className="mt-1 text-sm text-yellow-600">No orientation types found. Please create orientation types first.</p>
              )}
            </div>

            {/* Brand */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                className={`input-field ${errors.brand ? 'border-red-500' : ''}`}
                placeholder="Enter brand"
              />
              {errors.brand && (
                <p className="mt-1 text-sm text-red-600">{errors.brand}</p>
              )}
            </div>

            {/* Device Type (devType field) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Device Type (devType) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="devType"
                value={formData.devType}
                onChange={handleChange}
                className={`input-field ${errors.devType ? 'border-red-500' : ''}`}
                placeholder="Enter device type"
              />
              {errors.devType && (
                <p className="mt-1 text-sm text-red-600">{errors.devType}</p>
              )}
            </div>

            {/* Other */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Other Information
              </label>
              <textarea
                name="other"
                value={formData.other}
                onChange={handleChange}
                rows="3"
                className="input-field"
                placeholder="Enter other information"
              />
            </div>

            {/* Started At */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Started At
              </label>
              <input
                type="datetime-local"
                name="startedAt"
                value={formData.startedAt}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            {/* Ended At */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ended At
              </label>
              <input
                type="datetime-local"
                name="endedAt"
                value={formData.endedAt}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            {/* Active Status */}
            <div className="md:col-span-2">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Active</span>
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/devices/filter')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : id ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeviceForm;